import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {map, timeout} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {StartupResource} from '@services/resource/startup-resource';
import {ConfigService} from '@services/infra/config.service';
import {Startup} from '@services/model/startup';
import {ClientConfig} from '@app/client-config';
import {ErrorService} from '@services/error.service';
import {UUID} from '@util/types';
import {ContactResource} from '@services/resource/contact-resource';

@Injectable({
  providedIn: 'root',
})
export class StartupService {
  constructor(
    private readonly errors: ErrorService,
    private readonly http: HttpClient,
    private readonly conf: ConfigService,
  ) {}

  public read(uuid: string): Observable<StartupResource> {
    const requestUrl = `${this.conf.backendUrl}/v1/startup/${uuid}`;
    return this.http.get<StartupResource>(requestUrl).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler());
  }

  public update(id: string, startup: StartupResource): Observable<StartupResource> {
    const url = `${this.conf.backendUrl}/v1/startup/${id}`;
    return this.http.put<StartupResource>(url, startup, {withCredentials: true}).pipe();
  }

  public getStartups(sortBy: string, direction: 'asc' | 'desc', offset: number, limit: number): Observable<Startup[]> {
    const requestUrl = `${this.conf.backendUrl}/v1/startup`;
    const params = new HttpParams({
      fromObject: {
        sortBy,
        direction,
        offset: String(offset),
        limit: String(limit),
      },
    });
    return this.http.get<StartupResource[]>(requestUrl, {params, withCredentials: true}).pipe(
      timeout(ClientConfig.timeout),
      this.errors.withErrorHandler(),
      map((rs) => rs.map(Startup.fromResource)),
    );
  }

  public getStartupForUser(userId: string): Observable<Startup> {
    const url = `${this.conf.backendUrl}/v1/user/${userId}/startup`;
    return this.http.get<StartupResource>(url).pipe(
      timeout(ClientConfig.timeout),
      this.errors.withErrorHandler(),
      map((sr) => Startup.fromResource(sr)),
    );
  }

  public getStartupForProductId(productId: string): Observable<Startup> {
    const url = `${this.conf.backendUrl}/v1/product/${productId}/startup`;
    return this.http.get<StartupResource>(url).pipe(
      timeout(ClientConfig.timeout),
      this.errors.withErrorHandler(),
      map((sr) => Startup.fromResource(sr)),
    );
  }

  public getContactForStartup(startupId: UUID): Observable<ContactResource> {
    const url = `${this.conf.backendUrl}/v1/startup/${startupId}/contact`;
    return this.http.get<ContactResource>(url).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler());
  }

  public getFavoriteProductStatus(userId: UUID, productId: UUID): Observable<boolean> {
    const url = `${this.conf.backendUrl}/v1/user/${userId}/favorite-products/${productId}`;
    return this.http.get<boolean>(url).pipe();
  }

  public setFavoriteProduct(userId: UUID, productId: UUID): Observable<any> {
    const url = `${this.conf.backendUrl}/v1/user/${userId}/favorite-products/${productId}`;
    return this.http.put<void>(url, null).pipe();
  }

  public deleteFavoriteProduct(userId: UUID, productId: UUID): Observable<any> {
    const url = `${this.conf.backendUrl}/v1/user/${userId}/favorite-products/${productId}`;
    return this.http.delete<void>(url).pipe();
  }
}
